import { APIClient, getLoggedinUser } from "../helpers/api_helper";
import * as url from "../helpers/url_helper";
import type { PaginatedResponse } from "../types/api";

const api = new APIClient();

// Helper function to get tenantId from logged-in user
const getTenantId = (): string | null => {
    const authUser = getLoggedinUser();
    return authUser?.tenantId || null;
};

// Enums (matching API documentation)
export enum PurchaseOrderStatus {
    Draft = 1,
    Approved = 2,
    Received = 3,
    Cancelled = 4,
}

export const PurchaseOrderStatusLabels: Record<PurchaseOrderStatus, string> = {
    [PurchaseOrderStatus.Draft]: "Draft",
    [PurchaseOrderStatus.Approved]: "Approved",
    [PurchaseOrderStatus.Received]: "Received",
    [PurchaseOrderStatus.Cancelled]: "Cancelled",
};

export enum POLineStatus {
    Pending = 1,
    Received = 2,
}

export const POLineStatusLabels: Record<POLineStatus, string> = {
    [POLineStatus.Pending]: "Pending",
    [POLineStatus.Received]: "Received",
};

// PaymentTerms enum - matches API documentation
export enum PaymentTerms {
    Net30 = 1,          // API: 1 = Net30
    Net60 = 2,          // API: 2 = Net60
    DueOnReceipt = 3,   // API: 3 = DueOnReceipt
    CashOnDelivery = 4, // API: 4 = CashOnDelivery
}

export const PaymentTermsLabels: Record<PaymentTerms, string> = {
    [PaymentTerms.Net30]: "Net 30",
    [PaymentTerms.Net60]: "Net 60",
    [PaymentTerms.DueOnReceipt]: "Due on Receipt",
    [PaymentTerms.CashOnDelivery]: "Cash on Delivery",
};

// DeliveryMethod enum - matches API documentation
export enum DeliveryMethod {
    StandardDelivery = 1, // API: 1 = StandardDelivery
    ExpressDelivery = 2,  // API: 2 = ExpressDelivery
    Pickup = 3,          // API: 3 = Pickup
}

export const DeliveryMethodLabels: Record<DeliveryMethod, string> = {
    [DeliveryMethod.StandardDelivery]: "Standard Delivery",
    [DeliveryMethod.ExpressDelivery]: "Express Delivery",
    [DeliveryMethod.Pickup]: "Pick Up",
};

// Interfaces
export interface POLineItem {
    id?: string;
    productId: string;
    productCode?: string;
    productName?: string;
    quantity: number;
    quantityReceived?: number;
    quantityRemaining?: number;
    unitPrice: number;
    discountPercent?: number;
    lineTotal?: number;
    status?: POLineStatus;
    statusName?: string;
    notes?: string;
}

export interface PurchaseOrder {
    id: string;
    tenantId: string;
    poNumber: string;
    supplierId: string;
    supplierName?: string;
    orderDate: string;
    requiredDate?: string;
    status: PurchaseOrderStatus;
    statusName?: string;
    paymentTerms: PaymentTerms;
    paymentTermsName?: string;
    deliveryMethod: DeliveryMethod;
    deliveryMethodName?: string;
    deliveryAddress?: string;
    subTotal?: number;
    taxAmount?: number;
    total: number;
    totalItems?: number;
    receivedItems?: number;
    notes?: string;
    createdBy?: string;
    created?: string;
    modified?: string;
    lineItems?: POLineItem[];
}

export interface PurchaseOrderCreatePayload {
    tenantId?: string; // Optional - backend may extract from JWT token
    supplierId: string;
    poNumber?: string; // Optional - auto-generated if not provided
    orderDate: string;
    requiredDate?: string;
    paymentTerms: PaymentTerms;
    deliveryMethod: DeliveryMethod;
    deliveryAddress?: string;
    notes?: string;
    lineItems: POLineItemCreatePayload[];
}

export interface PurchaseOrderUpdatePayload {
    supplierId?: string;
    requiredDate?: string;
    paymentTerms?: PaymentTerms;
    deliveryMethod?: DeliveryMethod;
    deliveryAddress?: string;
    notes?: string;
}

export interface POLineItemCreatePayload {
    productId: string;
    quantity: number;
    unitPrice: number;
    discountPercent?: number;
    notes?: string;
}

export interface POLineItemUpdatePayload {
    quantity?: number;
    unitPrice?: number;
    discountPercent?: number;
    notes?: string;
}

// Service Methods
export const getPurchaseOrders = async (
    pageNumber: number = 1,
    pageSize: number = 50,
    status?: PurchaseOrderStatus
): Promise<PaginatedResponse<PurchaseOrder>> => {
    // v2.0 API: GET /api/v1/PurchaseOrder (paginated)
    // Returns PaginatedResponse<PurchaseOrder> or 204 NoContent
    // Backend filters by tenantId from JWT token automatically
    const queryParams: Record<string, string | number | undefined> = {
        pageNumber,
        pageSize,
    };

    if (status !== undefined) {
        queryParams.status = status;
    }

    const response = await api.get<PaginatedResponse<PurchaseOrder>>(url.GET_PURCHASE_ORDER_BY_ID, queryParams);
    
    // Handle 204 NoContent (already handled by axios interceptor)
    if (!response || !response.items) {
        return {
            items: [],
            pageNumber: 1,
            pageSize: pageSize,
            totalCount: 0,
            totalPages: 0,
        } as PaginatedResponse<PurchaseOrder>;
    }

    return response;
};

/**
 * Get Purchase Orders by Tenant (summary format, not paginated)
 * v2.0 API: GET /api/v1/PurchaseOrder/tenant/{tenantId}
 * Returns array of PurchaseOrder summaries
 */
export const getPurchaseOrdersByTenant = async (
    tenantId?: string | null,
    status?: PurchaseOrderStatus
): Promise<PurchaseOrder[]> => {
    const finalTenantId = tenantId || getTenantId();

    if (!finalTenantId) {
        return Promise.reject(new Error("Organization ID is required"));
    }

    const queryParams: Record<string, string | number | undefined> = {};
    if (status !== undefined) {
        queryParams.status = status;
    }

    const response = await api.get<PurchaseOrder[]>(`${url.GET_PURCHASE_ORDERS}/${finalTenantId}`, queryParams);
    return Array.isArray(response) ? response : [];
};

export const getPurchaseOrderById = async (id: string): Promise<PurchaseOrder> => {
    return api.get<PurchaseOrder>(`${url.GET_PURCHASE_ORDER_BY_ID}/${id}`);
};

export const getPurchaseOrdersBySupplier = async (supplierId: string): Promise<PurchaseOrder[]> => {
    // v2.0 API: GET /api/v1/PurchaseOrder/supplier/{supplierId}
    // Returns array (summary format), not paginated
    const response = await api.get<PurchaseOrder[]>(`${url.GET_PURCHASE_ORDERS_BY_SUPPLIER}/${supplierId}`);
    return Array.isArray(response) ? response : [];
};

export const createPurchaseOrder = async (po: PurchaseOrderCreatePayload): Promise<string> => {
    // Get tenantId if not provided
    const tenantId = po.tenantId || getTenantId();

    const payload: any = {
        supplierId: po.supplierId,
        orderDate: po.orderDate,
        paymentTerms: po.paymentTerms,
        deliveryMethod: po.deliveryMethod,
        lineItems: po.lineItems.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            discountPercent: item.discountPercent,
            notes: item.notes,
        })),
    };

    if (tenantId) {
        payload.tenantId = tenantId;
    }

    if (po.poNumber) {
        payload.poNumber = po.poNumber;
    }

    if (po.requiredDate) {
        payload.requiredDate = po.requiredDate;
    }

    if (po.deliveryAddress) {
        payload.deliveryAddress = po.deliveryAddress;
    }

    if (po.notes) {
        payload.notes = po.notes;
    }

    const response = await api.create<{ isSuccess: boolean; message: string; result: string; modelErrors?: any[] }>(url.CREATE_PURCHASE_ORDER, payload);

    // Handle wrapped response
    if (response?.isSuccess && response.result) {
        return response.result;
    }

    // Build error message with model errors if present
    let errorMessage = response?.message || "Failed to create purchase order";
    if (response?.modelErrors && response.modelErrors.length > 0) {
        const modelErrorMessages = response.modelErrors.map((err: any) => err.message || err).join(", ");
        errorMessage = `${errorMessage}. ${modelErrorMessages}`;
    }

    throw new Error(errorMessage);
};

export const updatePurchaseOrder = async (id: string, po: PurchaseOrderUpdatePayload): Promise<void> => {
    // v2.0 API: PUT /api/v1/PurchaseOrder/{id}
    // Response: { isSuccess: true, message: "Purchase order updated successfully" }
    // Note: id is in URL path, not in request body (though still accepted for backward compatibility)
    const payload: any = {};

    if (po.supplierId) {
        payload.supplierId = po.supplierId;
    }

    if (po.requiredDate) {
        payload.requiredDate = po.requiredDate;
    }

    if (po.paymentTerms !== undefined) {
        payload.paymentTerms = po.paymentTerms;
    }

    if (po.deliveryMethod !== undefined) {
        payload.deliveryMethod = po.deliveryMethod;
    }

    if (po.deliveryAddress) {
        payload.deliveryAddress = po.deliveryAddress;
    }

    if (po.notes) {
        payload.notes = po.notes;
    }

    const response = await api.put<{ isSuccess: boolean; message: string }>(`${url.UPDATE_PURCHASE_ORDER}/${id}`, payload);

    // Handle wrapped response - v2.0 API structure
    if (response && !response.isSuccess) {
        throw new Error(response.message || "Failed to update purchase order");
    }
};

export const deletePurchaseOrder = async (id: string): Promise<void> => {
    // v2.0 API: DELETE /api/v1/PurchaseOrder/{id}
    // Response: { isSuccess: true, message: "Purchase order deleted successfully" }
    const response = await api.delete<{ isSuccess: boolean; message: string }>(`${url.DELETE_PURCHASE_ORDER}/${id}`);
    
    // Handle wrapped response - v2.0 API structure
    if (response && !response.isSuccess) {
        throw new Error(response.message || "Failed to delete purchase order");
    }
};

export const approvePurchaseOrder = async (id: string): Promise<void> => {
    // v2.0 API: POST /api/v1/PurchaseOrder/{purchaseOrderId}/approve
    // Response: { isSuccess: true, message: "Purchase order approved successfully" }
    const response = await api.create<{ isSuccess: boolean; message: string }>(`${url.APPROVE_PURCHASE_ORDER}/${id}/approve`, {});

    // Handle wrapped response - v2.0 API structure
    if (response && !response.isSuccess) {
        throw new Error(response.message || "Failed to approve purchase order");
    }
};

export const receivePurchaseOrder = async (id: string): Promise<void> => {
    // v2.0 API: POST /api/v1/PurchaseOrder/{id}/receive
    // Response: { isSuccess: true, message: "Purchase order received successfully" }
    const response = await api.create<{ isSuccess: boolean; message: string }>(`${url.RECEIVE_PURCHASE_ORDER}/${id}/receive`, {});

    // Handle wrapped response - v2.0 API structure
    if (response && !response.isSuccess) {
        throw new Error(response.message || "Failed to receive purchase order");
    }
};

export const cancelPurchaseOrder = async (id: string): Promise<void> => {
    // v2.0 API: POST /api/v1/PurchaseOrder/{id}/cancel
    // Response: { isSuccess: true, message: "Purchase order cancelled successfully" }
    const response = await api.create<{ isSuccess: boolean; message: string }>(`${url.CANCEL_PURCHASE_ORDER}/${id}/cancel`, {});

    // Handle wrapped response - v2.0 API structure
    if (response && !response.isSuccess) {
        throw new Error(response.message || "Failed to cancel purchase order");
    }
};

export const addPOLineItem = async (purchaseOrderId: string, lineItem: POLineItemCreatePayload): Promise<string> => {
    const payload = {
        purchaseOrderId,
        productId: lineItem.productId,
        quantity: lineItem.quantity,
        unitPrice: lineItem.unitPrice,
        discountPercent: lineItem.discountPercent,
        notes: lineItem.notes,
    };

    const response = await api.create<{ isSuccess: boolean; message: string; result: string }>(url.ADD_PO_LINE_ITEM, payload);

    if (response?.isSuccess && response.result) {
        return response.result;
    }

    if (typeof response === 'string') {
        return response;
    }

    throw new Error(response?.message || "Failed to add line item");
};

export const updatePOLineItem = async (id: string, lineItem: POLineItemUpdatePayload): Promise<void> => {
    // v2.0 API: PUT /api/v1/PurchaseOrder/line-item
    // Response: { isSuccess: true, message: "..." }
    const payload: any = {
        id,
    };

    if (lineItem.quantity !== undefined) {
        payload.quantity = lineItem.quantity;
    }

    if (lineItem.unitPrice !== undefined) {
        payload.unitPrice = lineItem.unitPrice;
    }

    if (lineItem.discountPercent !== undefined) {
        payload.discountPercent = lineItem.discountPercent;
    }

    if (lineItem.notes) {
        payload.notes = lineItem.notes;
    }

    const response = await api.put<{ isSuccess: boolean; message: string }>(url.UPDATE_PO_LINE_ITEM, payload);

    // Handle wrapped response - v2.0 API structure
    if (response && !response.isSuccess) {
        throw new Error(response.message || "Failed to update line item");
    }
};

export const deletePOLineItem = async (id: string): Promise<void> => {
    // v2.0 API: DELETE /api/v1/PurchaseOrder/line-item/{lineId}
    // Response: { isSuccess: true, message: "..." }
    const response = await api.delete<{ isSuccess: boolean; message: string }>(`${url.DELETE_PO_LINE_ITEM}/${id}`);

    // Handle wrapped response - v2.0 API structure
    if (response && !response.isSuccess) {
        throw new Error(response.message || "Failed to delete line item");
    }
};

